import axios from 'axios';
import { API_URL, END_POINT_QUOTES } from '../const';

const inFlight = new Map();
const detailCache = new Map();
const optionsCache = new Map();
const MAX_DETAILS = 30;

export const quoteFilterParams = filters => Object.keys(filters).sort().reduce((result, name) => {
  const value = String(filters[name] ?? '').trim();
  if (value) result[name.replace(/\.name$/, '')] = value;
  return result;
}, {});

export const quoteQueryParams = (query, view = 'listing') => ({
  view,
  ...(view === 'listing' ? { page: query.page } : {}),
  filters: query.filters,
  sort_by: query.sort.key === 'index' ? 'id' : (query.sort.key || 'id').replace(/\.name$/, ''),
  sort_direction: query.sort.key ? (query.sort.direction || 'asc') : 'desc',
});

// Subscribers share only active requests, scoped by the current authentication token.
export const requestQuotes = (token, suffix = '', params = {}) => {
  const key = JSON.stringify([token, suffix, params]);
  let entry = inFlight.get(key);
  if (!entry) {
    const controller = new AbortController();
    entry = { controller, users: 0, timer: null };
    entry.promise = axios.get(API_URL + END_POINT_QUOTES + suffix, {
      params,
      headers: { Accept: 'application/json', Authorization: 'Bearer ' + token },
      signal: controller.signal,
    }).then(response => {
      if (response.data.status === false) throw new Error(response.data.message || 'Unable to load quotes.');
      return response.data;
    }).finally(() => {
      clearTimeout(entry.timer);
      if (inFlight.get(key) === entry) inFlight.delete(key);
    });
    inFlight.set(key, entry);
  }
  clearTimeout(entry.timer);
  entry.users += 1;
  let released = false;
  return {
    promise: entry.promise,
    release: () => {
      if (released) return;
      released = true;
      entry.users -= 1;
      if (!entry.users && inFlight.get(key) === entry) {
        // A remount in the same turn can subscribe without starting a second GET.
        entry.timer = setTimeout(() => {
          if (!entry.users) {
            entry.controller.abort();
            if (inFlight.get(key) === entry) inFlight.delete(key);
          }
        }, 0);
      }
    },
  };
};

export const isCurrentQuoteSession = token => sessionStorage.getItem('quotetoken')?.replace(/^"|"$/g, '') === token;
export const clearQuoteDataCache = () => { detailCache.clear(); optionsCache.clear(); };

const cacheKey = (token, id) => JSON.stringify([token, id]);
export const getQuoteDetail = (token, id) => detailCache.get(cacheKey(token, id));
export const cacheQuoteDetail = (token, quote) => {
  const key = cacheKey(token, quote.id);
  detailCache.delete(key);
  detailCache.set(key, quote);
  while (detailCache.size > MAX_DETAILS) detailCache.delete(detailCache.keys().next().value);
};
export const forgetQuoteDetail = (token, id) => detailCache.delete(cacheKey(token, id));
export const getQuoteOptions = token => optionsCache.get(token);
export const cacheQuoteOptions = (token, data) => {
  optionsCache.set(token, data);
  while (optionsCache.size > 2) optionsCache.delete(optionsCache.keys().next().value);
};

export const clearQuoteRequestCache = () => {
  inFlight.forEach(entry => { clearTimeout(entry.timer); entry.controller.abort(); });
  inFlight.clear();
  detailCache.clear();
  optionsCache.clear();
};
